// ── user-lookup — user id -> display name, for `created_by`-style fields ─────
// APIs return bare user ids; the UI must show a person, never a UUID. Framework-free
// on purpose: agent-sdk depends on neither React nor react-query (see
// query-client-defaults.ts), so consumers wrap `userLookupQueryOptions` in their own
// `useQuery`. Backed by gateway POST /v1/my/users/resolve (member-safe: org
// membership is the gate, so data_admin works). Ref: ASC-10699.

import { z } from 'zod'

import { safeFetchOrThrow } from './safe-fetch'

export const ResolvedUserSchema = z.object({
  user_id: z.string(),
  name: z.string(),
  initials: z.string(),
})

export const ResolveUsersResponseSchema = z.object({
  items: z.array(ResolvedUserSchema),
})

export type ResolvedUser = z.infer<typeof ResolvedUserSchema>

/** Resolved users keyed by id. An id absent from the map could not be resolved. */
export type UserLookup = ReadonlyMap<string, ResolvedUser>

/** Mirrors `MAX_RESOLVE_IDS` in platform_gateway/routes/users.py — more is a 422. */
const MAX_IDS_PER_REQUEST = 200

const RESOLVE_URL = '/api/my/users/resolve'

/**
 * Distinct, non-empty ids in stable order. Sorted so the same set of rows yields the
 * same query key regardless of row order, which keeps react-query from refetching.
 */
export function normalizeUserIds(ids: ReadonlyArray<string | null | undefined>): string[] {
  const unique = new Set<string>()
  for (const id of ids) {
    if (id) unique.add(id)
  }
  return [...unique].sort()
}

async function resolveChunk(ids: string[]): Promise<ResolvedUser[]> {
  const data = await safeFetchOrThrow(RESOLVE_URL, ResolveUsersResponseSchema, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
  return data.items
}

/** Throws on failure — react-query surfaces it as `error` and consumers render the fallback. */
export async function fetchResolvedUsers(ids: readonly string[]): Promise<UserLookup> {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
    chunks.push(ids.slice(i, i + MAX_IDS_PER_REQUEST))
  }
  const resolved = (await Promise.all(chunks.map(resolveChunk))).flat()
  return new Map(resolved.map((user) => [user.user_id, user]))
}

/**
 * Spread into `useQuery`: `useQuery(userLookupQueryOptions(rows.map((r) => r.createdBy)))`.
 * Disabled (no request) when there is nothing to resolve.
 */
export function userLookupQueryOptions(ids: ReadonlyArray<string | null | undefined>): {
  queryKey: readonly ['user-lookup', ...string[]]
  queryFn: () => Promise<UserLookup>
  enabled: boolean
  staleTime: number
} {
  const normalized = normalizeUserIds(ids)
  return {
    queryKey: ['user-lookup', ...normalized],
    queryFn: () => fetchResolvedUsers(normalized),
    enabled: normalized.length > 0,
    // Names change rarely; a stale one for five minutes is fine for a label.
    staleTime: 5 * 60 * 1000,
  }
}

const UNKNOWN_USER_LABEL = 'Unknown user'
const NO_USER_LABEL = '—'

/**
 * Text for a user cell. Never a raw id: `—` for no id or while the lookup is still
 * pending, `Unknown user` once it settled without a match (removed member, other org).
 */
export function getUserLabel(
  lookup: UserLookup | undefined,
  id: string | null | undefined,
  isPending: boolean,
): string {
  if (!id) return NO_USER_LABEL
  const user = getResolvedUser(lookup, id)
  if (user) return user.name
  return isPending ? NO_USER_LABEL : UNKNOWN_USER_LABEL
}

/**
 * Display identity for `id`, or `null` while loading / when unresolvable.
 * Callers render a person icon or a neutral label on `null` — NEVER the raw id.
 */
export function getResolvedUser(
  lookup: UserLookup | undefined,
  id: string | null | undefined,
): ResolvedUser | null {
  if (!lookup || !id) return null
  return lookup.get(id) ?? null
}
